import React, { useState, useEffect } from 'react';

/**
 * OptimizedImage Component
 * Features:
 * - Progressive loading (blur placeholder -> full image)
 * - Lazy loading
 * - WebP support with fallback
 * - Automatic thumbnail detection
 */
const OptimizedImage = ({
  src,
  alt,
  className = '',
  thumbnailSrc = null,
  width,
  height,
  objectFit = 'cover',
  loading = 'lazy',
  onLoad,
  fallbackSrc = null,
}) => {
  const [imageSrc, setImageSrc] = useState(thumbnailSrc || src);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // If we have a thumbnail, load the full image in the background
    if (thumbnailSrc && thumbnailSrc !== src) {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setImageSrc(src);
        setImageLoading(false);
        onLoad?.();
      };
      img.onerror = () => {
        setImageError(true);
        setImageLoading(false);
      };
    } else {
      // No thumbnail, just use the main src
      setImageSrc(src);
    }
  }, [src, thumbnailSrc, onLoad]);

  const handleImageLoad = () => {
    setImageLoading(false);
    if (!thumbnailSrc) {
      onLoad?.();
    }
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
    if (fallbackSrc) {
      setImageSrc(fallbackSrc);
    }
  };

  return (
    <div className={`relative overflow-hidden ${className}`} style={{ width, height }}>
      <img
        src={imageSrc}
        alt={alt}
        loading={loading}
        onLoad={handleImageLoad}
        onError={handleImageError}
        className={`w-full h-full transition-all duration-500 ${
          imageLoading ? 'blur-sm scale-105' : 'blur-0 scale-100'
        }`}
        style={{ objectFit }}
      />
      
      {/* Loading overlay */}
      {imageLoading && !imageError && (
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/10 to-[#F7931E]/10 animate-pulse" />
      )}
      
      {/* Error state */}
      {imageError && !fallbackSrc && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#FF6B35]/20 to-[#F7931E]/20">
          <span className="text-4xl opacity-50">🖼️</span>
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;
